// 章节提示词的阅读/批注/修改 —— 与 /api/chapters/:num/prompt* 交互的客户端封装。

export interface PromptAnnotation {
  id: number;
  chapter_num: string;
  user_id: number;
  /** 批注人的账号名，服务端 JOIN users 带出，仅用于展示。 */
  username: string;
  quote: string;
  comment: string;
  resolved: number;
  created_at: number;
}

async function parseJson(res: Response): Promise<any> {
  return res.json().catch(() => ({}));
}

function chapterPath(chapterNum: string): string {
  return encodeURIComponent(chapterNum);
}

export async function getPromptAnnotations(chapterNum: string): Promise<PromptAnnotation[]> {
  const res = await fetch(`/api/chapters/${chapterPath(chapterNum)}/prompt-annotations`, {
    credentials: "same-origin",
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "加载提示词批注失败");
  return data.annotations ?? [];
}

export async function addPromptAnnotation(
  chapterNum: string,
  quote: string,
  comment: string
): Promise<PromptAnnotation> {
  const res = await fetch(`/api/chapters/${chapterPath(chapterNum)}/prompt-annotations`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ quote, comment }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "添加批注失败");
  return data.annotation;
}

export async function setPromptAnnotationResolved(
  chapterNum: string,
  annotationId: number,
  resolved: boolean
): Promise<void> {
  const res = await fetch(`/api/chapters/${chapterPath(chapterNum)}/prompt-annotations/${annotationId}`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ resolved }),
  });
  if (!res.ok) throw new Error("更新批注状态失败");
}

export async function deletePromptAnnotation(chapterNum: string, annotationId: number): Promise<void> {
  const res = await fetch(`/api/chapters/${chapterPath(chapterNum)}/prompt-annotations/${annotationId}`, {
    method: "DELETE",
    credentials: "same-origin",
  });
  if (!res.ok) {
    const data = await parseJson(res);
    throw new Error(data.error || "删除批注失败");
  }
}

/** 只更新一章的提示词正文（仅管理员）。返回更新后的章节。 */
export async function savePromptContent(chapterNum: string, prompt: string): Promise<string> {
  const res = await fetch(`/api/chapters/${chapterPath(chapterNum)}/prompt`, {
    method: "PUT",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ prompt }),
  });
  const data = await parseJson(res);
  if (!res.ok) throw new Error(data.error || "保存提示词失败");
  return data.chapter?.prompt ?? prompt;
}

/** 触发提示词的 Word 导出下载。不传 chapterNum 则导出全部章节。
 *  走 <a download> 而不是 fetch + blob：文件名由服务端的 Content-Disposition 决定。 */
export function downloadPromptsDocx(chapterNum?: string): void {
  const query = chapterNum ? `?num=${encodeURIComponent(chapterNum)}` : "";
  const a = document.createElement("a");
  a.href = `/api/chapters/prompts-export${query}`;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
